
import { db } from './firebase';
import { collection, addDoc, updateDoc, deleteDoc, doc, getDocs, getDoc, query, where } from 'firebase/firestore';
import { auth } from './firebase';
import type { FunnelData } from '@/components/FunnelBuilder/types';
import { addCustomDomain, removeCustomDomain } from './vercel';

export const createFunnel = async (funnelData: FunnelData) => {
  if (!auth.currentUser) throw new Error('No authenticated user');
  
  const funnelsCollection = collection(db, 'funnels');
  const docRef = await addDoc(funnelsCollection, {
    ...funnelData,
    userId: auth.currentUser.uid,
    isPublished: false,
    subdomain: null,
    customDomain: null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  return docRef.id;
};

export const updateFunnel = async (funnelId: string, funnelData: Partial<FunnelData>) => {
  if (!auth.currentUser) throw new Error('No authenticated user');
  
  const funnelRef = doc(db, 'funnels', funnelId);
  const funnelSnap = await getDoc(funnelRef);
  
  if (!funnelSnap.exists() || funnelSnap.data().userId !== auth.currentUser.uid) {
    throw new Error('Unauthorized or funnel not found');
  }
  
  await updateDoc(funnelRef, {
    ...funnelData,
    updatedAt: new Date().toISOString(),
  });
};

export const publishFunnel = async (funnelId: string, subdomain: string, customDomain?: string) => {
  if (!auth.currentUser) throw new Error('No authenticated user');

  // Check if subdomain is already taken
  const funnelsCollection = collection(db, 'funnels');
  const q = query(funnelsCollection, where('subdomain', '==', subdomain));
  const querySnapshot = await getDocs(q);
  
  if (!querySnapshot.empty) {
    throw new Error('Subdomain already taken');
  }

  // If customDomain is provided, check if it's already taken
  if (customDomain) {
    const customDomainQuery = query(funnelsCollection, where('customDomain', '==', customDomain));
    const customDomainSnapshot = await getDocs(customDomainQuery);
    
    if (!customDomainSnapshot.empty) {
      throw new Error('Custom domain already in use');
    }

    // Add domain to Vercel
    try {
      await addCustomDomain(customDomain);
    } catch (error) {
      console.error('Error adding custom domain to Vercel:', error);
      throw new Error('Failed to configure custom domain');
    }
  }

  const funnelRef = doc(db, 'funnels', funnelId);
  const funnelSnap = await getDoc(funnelRef);
  
  if (!funnelSnap.exists() || funnelSnap.data().userId !== auth.currentUser.uid) {
    throw new Error('Unauthorized or funnel not found');
  }

  await updateDoc(funnelRef, {
    isPublished: true,
    subdomain,
    customDomain: customDomain || null,
    updatedAt: new Date().toISOString(),
  });
};

export const unpublishFunnel = async (funnelId: string) => {
  if (!auth.currentUser) throw new Error('No authenticated user');
  
  const funnelRef = doc(db, 'funnels', funnelId);
  const funnelSnap = await getDoc(funnelRef);
  
  if (!funnelSnap.exists() || funnelSnap.data().userId !== auth.currentUser.uid) {
    throw new Error('Unauthorized or funnel not found');
  }

  // Remove custom domain from Vercel if it exists
  const currentData = funnelSnap.data();
  if (currentData.customDomain) {
    try {
      await removeCustomDomain(currentData.customDomain);
    } catch (error) {
      console.error('Error removing custom domain from Vercel:', error);
      // Continue with unpublishing even if domain removal fails
    }
  }

  await updateDoc(funnelRef, {
    isPublished: false,
    subdomain: null,
    customDomain: null,
    updatedAt: new Date().toISOString(),
  });
};

export const deleteFunnel = async (funnelId: string) => {
  if (!auth.currentUser) throw new Error('No authenticated user');
  
  const funnelRef = doc(db, 'funnels', funnelId);
  const funnelSnap = await getDoc(funnelRef);
  
  if (!funnelSnap.exists() || funnelSnap.data().userId !== auth.currentUser.uid) {
    throw new Error('Unauthorized or funnel not found');
  }

  await deleteDoc(funnelRef);
};

export const getFunnels = async (): Promise<(FunnelData & { id: string })[]> => {
  if (!auth.currentUser) throw new Error('No authenticated user');
  
  const funnelsCollection = collection(db, 'funnels');
  const q = query(funnelsCollection, where('userId', '==', auth.currentUser.uid));
  const snapshot = await getDocs(q);
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as (FunnelData & { id: string })[];
};

export const getFunnel = async (funnelId: string): Promise<FunnelData & { id: string }> => {
  if (!auth.currentUser) throw new Error('No authenticated user');
  
  const funnelRef = doc(db, 'funnels', funnelId);
  const docSnap = await getDoc(funnelRef);
  
  if (!docSnap.exists() || docSnap.data().userId !== auth.currentUser.uid) {
    throw new Error('Unauthorized or funnel not found');
  }
  
  return {
    id: docSnap.id,
    ...docSnap.data()
  } as FunnelData & { id: string };
};

export const getFunnelBySubdomain = async (subdomain: string) => {
  console.log('Searching for funnel with subdomain:', subdomain);
  
  const funnelsCollection = collection(db, 'funnels');
  const q = query(
    funnelsCollection,
    where('subdomain', '==', subdomain),
    where('isPublished', '==', true)
  );
  
  try {
    const querySnapshot = await getDocs(q);
    console.log('Query results:', {
      empty: querySnapshot.empty,
      size: querySnapshot.size,
      docs: querySnapshot.docs.map(doc => ({
        id: doc.id,
        path: doc.ref.path,
        data: doc.data()
      }))
    });
    
    if (querySnapshot.empty) {
      // If no funnel found by subdomain, try custom domain
      const customDomainQuery = query(
        funnelsCollection,
        where('customDomain', '==', subdomain),
        where('isPublished', '==', true)
      );
      
      const customDomainSnapshot = await getDocs(customDomainQuery);
      
      if (customDomainSnapshot.empty) {
        console.log('No matching funnel found');
        return null;
      }
      
      const doc = customDomainSnapshot.docs[0];
      console.log('Found funnel by custom domain:', {
        id: doc.id,
        path: doc.ref.path,
        data: doc.data()
      });
      
      return {
        id: doc.id,
        ...doc.data()
      } as FunnelData & { id: string };
    }
    
    const doc = querySnapshot.docs[0];
    console.log('Found funnel:', {
      id: doc.id,
      path: doc.ref.path,
      data: doc.data()
    });
    
    return {
      id: doc.id,
      ...doc.data()
    } as FunnelData & { id: string };
  } catch (error) {
    console.error('Error fetching funnel:', error);
    return null;
  }
};
